import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { X, Search, Plus, Minus, ImageIcon, Lock, SlidersHorizontal, CalendarDays, Info } from 'lucide-react';
import { catalogAPI, miscAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthGate from '../AuthGate';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

// ── Calendar logic ──
// Orders on Sat/Sun are treated as placed on next Monday
// Minimum delivery date = effective order date + 3 weeks
// Delivery dates must be weekdays (Mon-Fri) and not holidays
function getEffectiveOrderDate() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = d.getDay();
  if (day === 0) d.setDate(d.getDate() + 1); // Sun → Mon
  if (day === 6) d.setDate(d.getDate() + 2); // Sat → Mon
  return d;
}

function toLocalDateStr(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function skipToNextBusinessDay(d, holidaySet) {
  let safe = 0;
  while (safe < 60) {
    const day = d.getDay();
    const ds = toLocalDateStr(d);
    if (day !== 0 && day !== 6 && !holidaySet.has(ds)) break;
    d.setDate(d.getDate() + 1);
    safe++;
  }
  return d;
}

function getMinDeliveryDate(holidaySet) {
  const effective = getEffectiveOrderDate();
  effective.setDate(effective.getDate() + 21); // +3 weeks
  skipToNextBusinessDay(effective, holidaySet);
  return effective;
}

function isWeekday(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Product Row ──
function PrebookRow({ product, qty, isHidden, onSetQty, onAdd, isEven }) {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const thumbRef = useRef(null);

  const handleMouseEnter = () => {
    const img = product.images?.[0];
    if (!img) return;
    const rect = thumbRef.current?.getBoundingClientRect();
    if (rect) setPopupPos({ top: rect.top - 10, left: rect.right + 12 });
    setShowPopup(true);
  };

  const getMinPrice = () => {
    if (!product.tier_pricing || product.tier_pricing.length === 0) return 0;
    return Math.min(...product.tier_pricing.map(t => t.price));
  };

  return (
    <>
      {/* Desktop */}
      <div
        data-testid={`prebook-row-${product._id}`}
        className={`hidden md:grid md:grid-cols-[1fr_48px_140px_80px_120px_140px_80px] items-center px-4 py-3 border-b border-[#f0efed] transition-colors hover:bg-[#fbfaf8] ${isEven ? '' : 'bg-[#fafaf9]'}`}
      >
        <Link to={`/product/${product.slug}`} className="text-sm font-medium text-[#2d2d2d] hover:text-[#2f6b4f] transition-colors truncate pr-2 uppercase">
          {product.name}
        </Link>
        <div ref={thumbRef} className="flex justify-center" onMouseEnter={handleMouseEnter} onMouseLeave={() => setShowPopup(false)}>
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.name} className="w-8 h-8 object-cover rounded border border-[#e7e3dd] cursor-pointer" />
          ) : (
            <div className="w-8 h-8 bg-[#f5f4f2] rounded border border-[#e7e3dd] flex items-center justify-center">
              <ImageIcon size={12} className="text-[#c0c0c0]" />
            </div>
          )}
        </div>
        <span className="text-xs text-[#6f6a63] truncate">{product.category || '-'}</span>
        <div className="text-center">
          {product.stock > 10 ? (
            <span className="text-xs text-[#2f6b4f] font-medium">{product.stock}</span>
          ) : product.stock > 0 ? (
            <span className="text-xs text-[#b28a4a] font-medium">{product.stock}</span>
          ) : (
            <span className="text-xs text-red-400 font-medium">0</span>
          )}
        </div>
        <div className="text-center">
          {isHidden ? (
            <Link to="/login" className="inline-flex items-center gap-1 text-[10px] text-[#2f6b4f] font-medium hover:underline">
              <Lock size={10} /> Log in
            </Link>
          ) : getMinPrice() > 0 ? (
            <span className="text-sm font-semibold text-[#2d2d2d]">${getMinPrice().toFixed(2)}</span>
          ) : (
            <span className="text-xs text-[#c0c0c0]">-</span>
          )}
        </div>
        <div className="flex items-center justify-center">
          {!isHidden && (
            <div className="flex items-center border border-[#e7e3dd] rounded">
              <button onClick={() => onSetQty(qty - 1)} className="px-1.5 py-1 hover:bg-gray-50 text-[#6f6a63]"><Minus size={12} /></button>
              <input type="number" value={qty} onChange={(e) => onSetQty(parseInt(e.target.value) || 1)} className="w-10 text-center text-xs border-x border-[#e7e3dd] py-1 focus:outline-none" min="1" />
              <button onClick={() => onSetQty(qty + 1)} className="px-1.5 py-1 hover:bg-gray-50 text-[#6f6a63]"><Plus size={12} /></button>
            </div>
          )}
        </div>
        <div className="flex justify-center">
          {!isHidden && product.stock > 0 && (
            <button data-testid={`prebook-add-btn-${product._id}`} onClick={onAdd} className="bg-[#2f3e36] text-white text-[10px] tracking-wider uppercase font-medium px-4 py-1.5 rounded hover:bg-[#253029] transition-colors active:scale-95">
              Add
            </button>
          )}
          {!isHidden && product.stock <= 0 && (
            <span className="text-[10px] text-red-500 font-medium">Sold Out</span>
          )}
        </div>
      </div>

      {/* Mobile */}
      <div className={`md:hidden px-4 py-3 border-b border-[#f0efed] ${isEven ? '' : 'bg-[#fafaf9]'}`}>
        <div className="flex items-center gap-3 mb-2">
          {product.images?.[0] ? (
            <img src={product.images[0]} alt={product.name} className="w-10 h-10 object-cover rounded border border-[#e7e3dd]" />
          ) : (
            <div className="w-10 h-10 bg-[#f5f4f2] rounded border border-[#e7e3dd] flex items-center justify-center"><ImageIcon size={14} className="text-[#c0c0c0]" /></div>
          )}
          <div className="flex-1 min-w-0">
            <Link to={`/product/${product.slug}`} className="text-sm font-medium text-[#2d2d2d] hover:text-[#2f6b4f] block truncate uppercase">{product.name}</Link>
            <span className="text-[10px] text-[#6f6a63]">{product.category}</span>
          </div>
        </div>
        {isHidden ? (
          <Link to="/login" className="inline-flex items-center gap-1 text-[11px] text-[#2f6b4f] font-medium hover:underline"><Lock size={11} /> Log in to see prices</Link>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-semibold">{getMinPrice() > 0 ? `$${getMinPrice().toFixed(2)}` : '-'}</span>
            <div className="flex items-center gap-2">
              <div className="flex items-center border border-[#e7e3dd] rounded">
                <button onClick={() => onSetQty(qty - 1)} className="px-1.5 py-1"><Minus size={12} /></button>
                <input type="number" value={qty} onChange={(e) => onSetQty(parseInt(e.target.value) || 1)} className="w-10 text-center text-xs border-x border-[#e7e3dd] py-1 focus:outline-none" min="1" />
                <button onClick={() => onSetQty(qty + 1)} className="px-1.5 py-1"><Plus size={12} /></button>
              </div>
              {product.stock > 0 ? (
                <button onClick={onAdd} className="bg-[#2f3e36] text-white text-[10px] tracking-wider uppercase font-medium px-3 py-1.5 rounded">Add</button>
              ) : (
                <span className="text-[10px] text-red-500">Sold Out</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image popup */}
      {showPopup && product.images?.[0] && (
        <div className="fixed z-[9999] pointer-events-none" style={{ top: `${popupPos.top}px`, left: `${popupPos.left}px`, transform: 'translateY(-50%)' }}>
          <div className="bg-white rounded-xl shadow-2xl border border-[#e7e3dd] overflow-hidden p-1">
            <img src={product.images[0]} alt={product.name} className="w-64 h-64 object-cover rounded-lg" />
          </div>
        </div>
      )}
    </>
  );
}

// ── Main Page ──
export default function PrebookPage() {
  return (
    <AuthGate>
      <PrebookPageContent />
    </AuthGate>
  );
}

function PrebookPageContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [catFilterText, setCatFilterText] = useState('');
  const [colorFilterText, setColorFilterText] = useState('');
  const { user } = useAuth();
  const { addToCart } = useCart();

  // Holidays
  const [holidays, setHolidays] = useState([]);
  const holidaySet = new Set(holidays.map(h => h.date));

  useEffect(() => {
    catalogAPI.getHolidays().then(res => setHolidays(res.data.holidays || [])).catch(() => {});
  }, []);

  const currentCategory = searchParams.get('category') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentColor = searchParams.get('color') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');

  // Delivery date
  const minDelivery = getMinDeliveryDate(holidaySet);
  const minDateStr = toLocalDateStr(minDelivery);
  const [deliveryDate, setDeliveryDate] = useState('');

  // Update when holidays load
  useEffect(() => {
    const newMin = toLocalDateStr(getMinDeliveryDate(holidaySet));
    setDeliveryDate(prev => (!prev || prev < newMin) ? newMin : prev);
  }, [holidays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize
  useEffect(() => { setDeliveryDate(minDateStr); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDateChange = (val) => {
    if (val < minDateStr) {
      toast.error('Minimum delivery date is 3 weeks from today');
      return;
    }
    if (!isWeekday(val)) {
      toast.error('Please select a weekday (Mon-Fri). Weekend orders are processed as Monday.');
      return;
    }
    if (holidaySet.has(val)) {
      const label = holidays.find(h => h.date === val)?.label;
      toast.error(`${formatDisplayDate(val)}${label ? ` (${label})` : ''} is a non-working day. Please select another date.`);
      return;
    }
    setDeliveryDate(val);
  };

  // Today info for the notice
  const todayDay = new Date().getDay();
  const isWeekend = todayDay === 0 || todayDay === 6;

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 30, is_prebook_eligible: true };
      if (currentCategory) params.category = currentCategory;
      if (currentSearch) params.search = currentSearch;
      if (currentColor) params.color = currentColor;
      const res = await catalogAPI.getProducts(params);
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentCategory, currentSearch, currentColor, currentPage]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    catalogAPI.getCategories().then(res => setCategories(res.data.categories)).catch(() => {});
  }, []);

  const updateFilter = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    if (key !== 'page') p.set('page', '1');
    setSearchParams(p);
    window.scrollTo(0, 0);
  };

  const clearFilters = () => setSearchParams({});

  const [colors, setColors] = useState([]);
  useEffect(() => {
    miscAPI.getColors().then(r => setColors(r.data.colors)).catch(() => {});
  }, []);
  const hasActiveFilters = currentCategory || currentSearch || currentColor;

  // Per-row quantities
  const [quantities, setQuantities] = useState({});
  const setQty = (id, val) => setQuantities(prev => ({ ...prev, [id]: Math.max(1, val) }));
  const getQty = (id) => quantities[id] || 1;

  const handleAddRow = async (productId) => {
    if (!user) return;
    if (!deliveryDate) { toast.error('Please select a delivery date'); return; }
    const qty = getQty(productId);
    try {
      await addToCart(productId, qty, 'catalog', deliveryDate);
      toast.success('Added to cart!');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to add'));
    }
  };

  return (
    <div className="pw-container py-2">
      <Breadcrumbs items={[{ label: 'Prebook Orders' }]} />

      <div className="flex gap-8">
        {/* Sidebar */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-28">
            <h3 className="text-sm font-semibold tracking-wider uppercase mb-4">Filters</h3>

            <div className="mb-5">
              <label className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] block mb-2">Search</label>
              <div className="relative">
                <input data-testid="prebook-search-input" type="text" value={currentSearch} onChange={(e) => updateFilter('search', e.target.value)} placeholder="Search products..." className="w-full pl-8 pr-3 py-2 border border-[#e7e3dd] rounded-lg text-xs bg-white focus:outline-none focus:border-[#2f6b4f]" />
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6f6a63]" />
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Category</h4>
              <input type="text" value={catFilterText} onChange={(e) => setCatFilterText(e.target.value)} placeholder="Filter categories..." className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-[10px] mb-2 focus:outline-none focus:border-[#2f6b4f]" style={{ fontSize: '16px', touchAction: 'manipulation' }} />
              <div className="space-y-1 max-h-48 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                {categories.filter(c => !catFilterText || c.name.toLowerCase().includes(catFilterText.toLowerCase())).map(cat => (
                  <button key={cat._id} onClick={() => updateFilter('category', currentCategory === cat.name ? '' : cat.name)} className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${currentCategory === cat.name ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}>
                    {cat.name} ({cat.product_count})
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Color</h4>
              <input type="text" value={colorFilterText} onChange={(e) => setColorFilterText(e.target.value)} placeholder="Filter colors..." className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-[10px] mb-2 focus:outline-none focus:border-[#2f6b4f]" style={{ fontSize: '16px', touchAction: 'manipulation' }} />
              <div className="space-y-1 max-h-40 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                {colors.filter(c => !colorFilterText || c.toLowerCase().includes(colorFilterText.toLowerCase())).map(color => (
                  <button key={color} onClick={() => updateFilter('color', currentColor === color ? '' : color)} className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${currentColor === color ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}>
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-700 underline">Clear all filters</button>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h1 className="text-lg md:text-xl font-semibold tracking-wider uppercase animate-pulse-subtle">Prebook Orders</h1>
              <span className="text-sm text-[#6f6a63]">({total} products)</span>
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden flex items-center gap-1.5 px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm">
              <SlidersHorizontal size={14} /> Filters
            </button>
          </div>

          {/* Delivery Date Calendar */}
          {user && (
            <div data-testid="prebook-delivery-section" className="bg-white border border-[#e7e3dd] rounded-xl px-5 py-4 mb-6">
              <div className="flex items-center gap-5 flex-wrap">
                <div className="flex items-center gap-2">
                  <CalendarDays size={18} className="text-[#2f6b4f]" />
                  <span className="text-sm font-semibold text-[#2d2d2d]">Delivery Date</span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="prebook-delivery-date"
                    type="date"
                    value={deliveryDate}
                    min={minDateStr}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f] cursor-pointer"
                  />
                  <span className="text-xs text-[#6f6a63]">{formatDisplayDate(deliveryDate)}</span>
                </div>
              </div>
              <div className="flex items-start gap-2 mt-3 p-3 bg-[#fbfaf8] rounded-lg">
                <Info size={14} className="text-[#2f6b4f] flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-[#6f6a63] leading-relaxed">
                  <p><strong>Prebook orders require a minimum of 3 weeks advance notice.</strong> Earliest available: <strong>{formatDisplayDate(minDateStr)}</strong></p>
                  {isWeekend && <p className="mt-1 text-[#b28a4a] font-medium">Today is a weekend — your order will be processed starting next Monday.</p>}
                </div>
              </div>
            </div>
          )}

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {currentCategory && <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">{currentCategory} <X size={12} className="cursor-pointer" onClick={() => updateFilter('category', '')} /></span>}
              {currentColor && <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">{currentColor} <X size={12} className="cursor-pointer" onClick={() => updateFilter('color', '')} /></span>}
              {currentSearch && <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">"{currentSearch}" <X size={12} className="cursor-pointer" onClick={() => updateFilter('search', '')} /></span>}
            </div>
          )}

          {/* Products Table */}
          {loading ? (
            <div className="space-y-3">{[...Array(8)].map((_, i) => <div key={i} className="animate-pulse bg-[#fbfaf8] rounded-lg h-14" />)}</div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#6f6a63] mb-4">No products found.</p>
              <button onClick={clearFilters} className="text-[#2f6b4f] underline text-sm">Clear filters</button>
            </div>
          ) : (
            <>
              <div className="bg-white border border-[#e7e3dd] rounded-xl overflow-hidden">
                <div className="hidden md:grid md:grid-cols-[1fr_48px_140px_80px_120px_140px_80px] items-center bg-[#fbfaf8] border-b border-[#e7e3dd] px-4 py-2.5">
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Product</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Image</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63]">Category</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">Stock</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">{user ? 'Unit Price' : ''}</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center">{user ? 'Qty' : ''}</span>
                  <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] text-center"></span>
                </div>
                {products.map((product, idx) => (
                  <PrebookRow
                    key={product._id}
                    product={product}
                    qty={getQty(product._id)}
                    isHidden={!user || product.prices_hidden}
                    onSetQty={(val) => setQty(product._id, val)}
                    onAdd={() => handleAddRow(product._id)}
                    isEven={idx % 2 === 0}
                  />
                ))}
              </div>

              {total > 30 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: Math.ceil(total / 30) }, (_, i) => i + 1).map(p => (
                    <button key={p} onClick={() => updateFilter('page', String(p))} className={`w-8 h-8 rounded text-sm ${currentPage === p ? 'bg-[#2d2d2d] text-white' : 'border border-[#e7e3dd] hover:bg-gray-50'}`}>{p}</button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Filters */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowFilters(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-semibold tracking-wider uppercase">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X size={20} /></button>
            </div>
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Category</h4>
              {categories.map(cat => (
                <button key={cat._id} onClick={() => { updateFilter('category', currentCategory === cat.name ? '' : cat.name); setShowFilters(false); }} className={`block w-full text-left text-sm py-1.5 ${currentCategory === cat.name ? 'text-[#2f6b4f] font-medium' : 'text-[#6f6a63]'}`}>{cat.name}</button>
              ))}
            </div>
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Color</h4>
              {colors.map(c => (
                <button key={c} onClick={() => { updateFilter('color', currentColor === c ? '' : c); setShowFilters(false); }} className={`block w-full text-left text-sm py-1.5 ${currentColor === c ? 'text-[#2f6b4f] font-medium' : 'text-[#6f6a63]'}`}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
